import React, { useRef } from "react";
import { WorkInstruction } from "../../types/api/work-instruction/get-work-instructions-success-response";
import { WaversurferAudioPlayer } from "../wavesurfer-audio-player/wavesurfer-audio-player";
import { getImageUrl } from "../../../../utils/image-helper/get-image-url";
import { useFiles } from "../../../files/hooks/use-files";
import { Modal, ModalBody } from "reactstrap";
import { useToggle } from "../../../../hooks/use-toggle";
import { FileList } from "../../../files/components/file-list/file-list";
import { networkCall } from "../../../../utils/network-call/network-call";
import { useDispatch } from "react-redux";
import { showDefaultErrorMessages } from "../../../../utils/show-default-error-message/show-default-error-messages";
import { AlertMessageState, clearAlertMessage, showAlertMessage } from "../../../../redux/reducer/alert-message.reducer";
import { AlertMessages } from "../../../../enums/alert-messages.enum";
import { AxiosResponse } from "axios";
import { CreateWorkInstructionSuccessResponse } from "../../types/api/work-instruction/create-work-instructions-success-response";
import { DefaultErrorResponse } from "../../../../types/api/error/default-error-response/default-error-respose.type";
import { ValidationErrorResponseData } from "../../../../types/api/error/validation-error-response-data/validation-error-response-data.type.";
import { CMSButton } from "../../../../components/cms/button/cms-button.";
import { showLoadingAlertMessage } from "../../../../utils/show-loading-alert-message/show-loading-alert-message";
import { CreateFilesSuccessResponse } from "../../../files/types/api/create-files-success-response";
import { FileType } from "../../../files/types/api/get-file-with-siblings-success-response";
import { usePreventScrollWhenModalIsOpen } from "../../../../hooks/use-prevent-scroll-when-modal-is-open";

type AudioUploaderProps = {
    workInstruction: WorkInstruction;
    onSavedWorkInstruction: (editedWorkInstruction: WorkInstruction) => void;
};

export const AudioUploader = ({ workInstruction, onSavedWorkInstruction }: AudioUploaderProps) => {
    const {
        files,
        goBackByOneFolder,
        openFolder,
        parentFolderId,
        addFiles
    } = useFiles();
    const [isFileChooserModalOpen, toggleIsFileChooserModalOpen] = useToggle(false);
    const dispatch = useDispatch();
    const fileInputRef = useRef<HTMLInputElement>(null);

    usePreventScrollWhenModalIsOpen(isFileChooserModalOpen);

    const handleDeleteClick = async () => {
        if (!window.confirm("Biztosan törlöd a fájlt?")) return;

        const response = await networkCall(`/work-instructions/${ workInstruction.id }`, {
            method: 'PUT',
            body: {
                audioFileId: 0
            }
        });

        const messageShowed = showDefaultErrorMessages(response);
        if(messageShowed) return;

        dispatch(clearAlertMessage());
        const alertMessageState: AlertMessageState = {
            alertMessageType: 'error',
            alertMessageText: AlertMessages.UnknownErrorTryAgain
        };

        if (response?.status === 200) {
            dispatch(showAlertMessage({
                alertMessageText: AlertMessages.DeletedSuccessfully
            }));

            onSavedWorkInstruction({
                ...workInstruction,
                audioFile: null
            });

            return;
        } else if (response?.status === 400) {
            const res: AxiosResponse<DefaultErrorResponse> = response;

            switch (res.data.code) {
                case 1000: {
                    const _res: AxiosResponse<DefaultErrorResponse<ValidationErrorResponseData[]>> = response;
                    if (_res.data.data && _res.data.data.length > 0) {
                        for (const error of _res.data.data) {
                            let alertMessage;
                            if (error.field === 'audioFileId') alertMessage = 'Hangfájl:';
                            alertMessageState.alertMessageText = `${ alertMessage } ${ error.errors.join(', ') }`;
                            break;
                        }
                    }
                    break;
                }
            }
        } else if (response?.status === 404) {
            const res: AxiosResponse<DefaultErrorResponse> = response;

            switch (res.data.code) {

                case 6000:
                    alertMessageState.alertMessageText = 'A lépés nem létezik.';
                    break;

            }
        }

        dispatch(showAlertMessage(alertMessageState));
    };

    const handleFileSelect = async (id: number) => {
        const response = await networkCall(`/work-instructions/${ workInstruction.id }`, {
            method: 'PUT',
            body: {
                audioFileId: id
            }
        });

        const messageShowed = showDefaultErrorMessages(response);
        if(messageShowed) return;

        dispatch(clearAlertMessage());
        const alertMessageState: AlertMessageState = {
            alertMessageType: 'error',
            alertMessageText: AlertMessages.UnknownErrorTryAgain
        };

        if (response?.status === 200) {
            dispatch(showAlertMessage({
                alertMessageText: AlertMessages.SavedSuccessfully
            }));
            toggleIsFileChooserModalOpen();

            const res: AxiosResponse<CreateWorkInstructionSuccessResponse> = response;
            onSavedWorkInstruction({
                ...workInstruction,
                audioFile: res.data.workInstruction.audioFile
            });

            return;
        } else if (response?.status === 400) {
            const res: AxiosResponse<DefaultErrorResponse> = response;

            switch (res.data.code) {
                case 1000: {
                    const _res: AxiosResponse<DefaultErrorResponse<ValidationErrorResponseData[]>> = response;
                    if (_res.data.data && _res.data.data.length > 0) {
                        for (const error of _res.data.data) {
                            let alertMessage;
                            if (error.field === 'audioFileId') alertMessage = 'Hangfájl:';
                            alertMessageState.alertMessageText = `${ alertMessage } ${ error.errors.join(', ') }`;
                            break;
                        }
                    }
                    break;
                }
            }
        } else if (response?.status === 404) {
            const res: AxiosResponse<DefaultErrorResponse> = response;

            switch (res.data.code) {

                case 6000:
                    alertMessageState.alertMessageText = 'A lépés nem létezik.';
                    break;

            }
        }

        dispatch(showAlertMessage(alertMessageState));
    };

    const handleNewFileUploadChange: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
        if (!e.target.files || e.target.files.length === 0) return;

        showLoadingAlertMessage();
        
        const response = await networkCall('/files', {
            method: 'POST',
            requestBodyType: 'FILE_FORM',
            body: {
                files: e.target.files,
                parentId: parentFolderId === null ? 0 : parentFolderId,
                type: FileType.SimpleFile
            }
        });

        const messageShowed = showDefaultErrorMessages(response);
        if (messageShowed) return;

        dispatch(clearAlertMessage());
        const alertMessageState: AlertMessageState = {
            alertMessageType: 'error',
            alertMessageText: AlertMessages.UnknownErrorTryAgain
        };

        if (response?.status === 201) {
            const res: AxiosResponse<CreateFilesSuccessResponse> = response;
            alertMessageState.alertMessageText = 'Mentés sikeres.';
            alertMessageState.alertMessageType = 'success';
            addFiles(res.data.files);
            handleFileSelect(res.data.files[0].id);
            return;
        }

        dispatch(showAlertMessage(alertMessageState));
    };

    return (
        <>
            <div style={{ color: '#8B68DD' }}><strong>Audio</strong></div>
            {
                workInstruction.audioFile ? (
                    <>
                        <div style={{ display: 'flex', gap: '25px', transform: 'translateY(-15px)', alignItems: 'center' }}>
                            <WaversurferAudioPlayer src={workInstruction.audioFile.url ?? ''} waveColor='#5904C5' height={25} />
                            <div>
                                <img
                                    title='Feltöltött fájl törlése'
                                    style={{ width: '55px', display: 'block', marginBottom: '-15px', cursor: 'pointer' }}
                                    src={getImageUrl('delete-icon3.png')}
                                    alt="delete-icon"
                                    onClick={handleDeleteClick}
                                />
                                <img
                                    title='Feltöltés'
                                    style={{ width: '55px', display: 'block', cursor: 'pointer' }}
                                    src={getImageUrl('file-upload-icon2.png')}
                                    alt="file-upload-icon"
                                    onClick={toggleIsFileChooserModalOpen}
                                />
                            </div>
                        </div>
                    </>
                ) : (
                    <img
                        className="mt-1"
                        title='Feltöltés'
                        style={{ width: '55px', display: 'block', cursor: 'pointer' }}
                        src={getImageUrl('file-upload-icon2.png')}
                        alt="file-upload-icon"
                        onClick={toggleIsFileChooserModalOpen}
                    />
                )
            }
            <Modal isOpen={isFileChooserModalOpen} toggle={toggleIsFileChooserModalOpen} size="lg" scrollable>
                <div className="d-flex justify-content-between blue-font" style={{ padding: '15px 15px 0 15px' }}>
                    <h3>Fájlválasztás</h3>
                    <img
                        style={{ width: '35px', height: '35px' }}
                        src={getImageUrl('close-button.png')}
                        alt="close-button"
                        title="Bezárás"
                        className="cursor-pointer"
                        onClick={toggleIsFileChooserModalOpen}
                    />
                </div>
                <ModalBody>
                    <FileList
                        type="fileChooser"
                        files={files}
                        onFolderOpen={openFolder}
                        onGoBackByOneFolder={goBackByOneFolder}
                        hasParentFolder={parentFolderId !== null}
                        onSelect={handleFileSelect}
                    />
                    <div className="text-end mt-3">
                        <CMSButton
                            text="Új fájl feltöltése"
                            iconSrc={getImageUrl('add-file-icon.png')}
                            onClick={() => {
                                if (fileInputRef) {
                                    alert('A fájl a fájlválasztóban kiválasztott mappába fog feltöltődni.');
                                    fileInputRef.current?.click();
                                }
                            }}
                        />
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept='audio/*'
                            style={{ display: 'none' }}
                            onChange={handleNewFileUploadChange}
                        />
                    </div>
                </ModalBody>
            </Modal>
        </>
    );
};